import { useAuthContext } from "@hooks/context/useAuthContext";
import { useProfile } from "@hooks/crud/profile/useProfile";
import { FormControl, Grid, Skeleton, Stack } from "@mui/material";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import { accents } from "@themes/tokens/accents";
import { BaseButton } from "@ui/atoms/BaseButton/BaseButton";
import { BaseTextField } from "@ui/molecules/BaseTextField/BaseTextField";
import {
  FormComponentsErrorBox,
  FormSectionTitle
} from "@ui/molecules/FormsComponents/FormsComponents";
import { inputTrasparentStyles } from "@themes/overrides/inputs";
import { catchSentryError } from "@utils/sentry";
import { showToast } from "@utils/toast";
import {
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup
} from "aws-amplify/auth";
import QRCode from "qrcode";
import { useFormik } from "formik";
import { KeyboardEvent, useEffect, useState } from "react";
import * as Yup from "yup";

// Forced TOTP enrolment for a user whose org requires MFA but who has not yet
// enrolled. Full-screen and NON-dismissable: there is no cancel / back-to-app
// path — enrolment must complete before the user reaches the app. MFA is
// org-controlled, so there is no self-service opt-out here.
const MfaSetupForm = () => {
  const { completeMfaSignIn, userProfile } = useAuthContext();
  const { updateProfile } = useProfile();
  const [setupUri, setSetupUri] = useState<string>();
  const [sharedSecret, setSharedSecret] = useState<string>();
  const [qrDataUrl, setQrDataUrl] = useState<string>();
  const [setupError, setSetupError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const details = await setUpTOTP();
        if (cancelled) {
          return;
        }
        setSharedSecret(details.sharedSecret);
        // getSetupUri(appName, accountName?) returns the otpauth:// URL the
        // authenticator app encodes as a QR.
        const uri = details
          .getSetupUri("Catchment", userProfile?.email ?? undefined)
          .toString();
        setSetupUri(uri);
        // Render the QR with the SocialPro brand colour, matching the QR codes
        // in our emails (`accents.blueDark` on white). A fixed indigo rather
        // than the theme primary: this is rasterised to a PNG, so it cannot
        // read a CSS token, and the emails it matches have no theme. The secret
        // below is the manual fallback for anyone who can't scan.
        const qr = await QRCode.toDataURL(uri, {
          color: { dark: accents.blueDark, light: "#ffffff" },
          errorCorrectionLevel: "M",
          margin: 2,
          width: 220
        });
        if (!cancelled) {
          setQrDataUrl(qr);
        }
      } catch (e) {
        if (!cancelled) {
          setSetupError(
            "Could not start authenticator setup. Please try again later."
          );
        }
        catchSentryError(e);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [userProfile?.email]);

  const formik = useFormik({
    initialValues: {
      code: "",
      submit: null
    },
    onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
      try {
        await verifyTOTPSetup({ code: values.code.trim() });
        await updateMFAPreference({ totp: "PREFERRED" });
        // TOTP is now live at Cognito — that is the source of truth for future
        // challenges. Persisting mfaEnrolled=true is only a login-gate
        // optimisation, and its API call is itself MFA-gated mid-session, so it
        // is best-effort: a failure here must NOT strand the user on this screen
        // after they have correctly enrolled. Re-submitting would re-run
        // verifyTOTPSetup on an already-verified secret and throw, so blocking
        // app entry on this write is a hard lock-out.
        if (userProfile) {
          try {
            await updateProfile({ ...userProfile, mfaEnrolled: true });
          } catch (e) {
            catchSentryError(e);
          }
        }
        const authed = await completeMfaSignIn();
        if (!authed) {
          setStatus({ success: false });
          setErrors({
            submit:
              "An error occurred. Please contact support or try again later."
          });
          return;
        }
        showToast("Two-factor authentication enabled.");
        // On success the app re-renders on loggedIn and routes away.
      } catch (err: unknown) {
        setStatus({ success: false });
        setErrors({
          submit:
            err instanceof Error
              ? err.message
              : "Invalid code. Please try again."
        });
        catchSentryError(err);
      } finally {
        setSubmitting(false);
      }
    },
    validationSchema: Yup.object().shape({
      code: Yup.string()
        .matches(/^\d{6}$/, "Enter the 6-digit code")
        .required("Code is required")
    })
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && !formik.isSubmitting) {
      e.preventDefault();
      formik.handleSubmit();
    }
  };

  return (
    <form noValidate onSubmit={formik.handleSubmit} onKeyDown={handleKeyDown}>
      <Grid
        container
        spacing={2}
        sx={{
          mt: 2,
          width: "100%"
        }}
      >
        <Grid size={12}>
          <FormSectionTitle
            title="Set up two-factor authentication"
            description="Your organisation requires an authenticator app. Scan the code below in your app, then enter the 6-digit code it shows."
            loading={!setupUri && !setupError}
          />
        </Grid>

        {setupError ? (
          <Grid size={12}>
            <FormComponentsErrorBox error>{setupError}</FormComponentsErrorBox>
          </Grid>
        ) : (
          <>
            <Grid size={12}>
              <Stack spacing={2} sx={{ alignItems: "center" }}>
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt="Authenticator setup QR code"
                    width={220}
                    height={220}
                    style={{ borderRadius: 8 }}
                  />
                ) : (
                  <Skeleton variant="rounded" width={220} height={220} />
                )}
              </Stack>
            </Grid>
            {/* Manual fallback: for screen-reader users, same-device setup, or a
                QR that won't scan. The secret is also the E2E test hook. */}
            <Grid size={12}>
              <Stack spacing={1} sx={{ alignItems: "center" }}>
                <BaseTypography sx={{ color: "text.secondary", fontSize: 12 }}>
                  Can&apos;t scan? Enter this key manually:
                </BaseTypography>
                <BaseTypography
                  data-test-id="mfa-totp-secret"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 14,
                    letterSpacing: 1,
                    userSelect: "all",
                    wordBreak: "break-all"
                  }}
                >
                  {sharedSecret}
                </BaseTypography>
              </Stack>
            </Grid>
          </>
        )}

        <Grid size={12}>
          <FormControl fullWidth>
            <BaseTextField
              id="code"
              type="text"
              loading={false}
              disabled={formik.isSubmitting || !setupUri}
              value={formik.values.code}
              labelText="Authentication code"
              name="code"
              maxLength={6}
              dataTestId="mfa-setup-code"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              placeholder="123456"
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code ? formik.errors.code : ""}
              sx={inputTrasparentStyles}
            />
            {formik.errors.submit && (
              <FormComponentsErrorBox>
                <BaseTypography
                  data-test-id="mfa-setup-error"
                  color="error"
                  sx={{
                    fontSize: 12,
                    mt: 1
                  }}
                >
                  {formik.errors.submit}
                </BaseTypography>
              </FormComponentsErrorBox>
            )}
          </FormControl>
        </Grid>

        <Grid size={12}>
          <BaseButton
            id="submit_button"
            disabled={formik.isSubmitting || !setupUri}
            text="Verify and continue"
            type="submit"
          />
        </Grid>
      </Grid>
    </form>
  );
};

export default MfaSetupForm;
